import { useState } from 'react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import LookupSelect from '../components/LookupSelect'

interface ItemEstorno {
  linha: number; itemCod: number; itemDes: string; quantidade: number; valorTotal: number
}

interface OrdemServicoEstorno {
  ordnum: number; acao: 'apaga_os' | 'apaga_linha'; ordlin?: number
}

interface NotaEstorno {
  empcod: number; serie: string; numero: number; cliCod: string; cliNom: string
  modelo: string; chave: string | null
  dataEmissao: string | null; dataEntrada: string | null
  ftvTipdco: string; ftvNumero: number
  valorTotal: number
  itens: ItemEstorno[]
  ordensServico: OrdemServicoEstorno[]
  bloqueada: boolean
  motivoBloqueio?: string
  avisoPeriodo?: string
}

function fmtMoeda(v: number) {
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function fmtQtd(v: number) {
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
}
function fmtDataBr(v: string | null) {
  if (!v) return '  /  /  '
  return v.slice(0, 10).split('-').reverse().join('/')
}

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
  fontSize: '0.85rem', height: 32, background: '#fff',
}
const label: React.CSSProperties = { fontSize: '0.72rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }

function Campo({ rotulo, width, children }: { rotulo: string; width?: number; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width }}>
      <label style={label}>{rotulo}</label>
      {children}
    </div>
  )
}

export default function EstornoNotaFiscal() {
  const { empresaCod } = useEmpresa()
  const [chave, setChave] = useState('')
  const [serie, setSerie] = useState('')
  const [numero, setNumero] = useState('')
  const [fornecedorCod, setFornecedorCod] = useState('')
  const [fornecedorDes, setFornecedorDes] = useState('')

  const [buscando, setBuscando] = useState(false)
  const [erro, setErro] = useState('')
  const [nota, setNota] = useState<NotaEstorno | null>(null)
  const [confirmando, setConfirmando] = useState(false)
  const [estornando, setEstornando] = useState(false)
  const [sucesso, setSucesso] = useState('')

  async function buscar() {
    setErro(''); setSucesso(''); setNota(null); setConfirmando(false)
    const chaveDigitos = chave.replace(/\D/g, '')
    // Série pode ficar em branco (nota sem série)
    if (!chaveDigitos && !numero.trim()) {
      setErro('Informe a Chave NF-e/CT-e/NFS-e, ou o Número (Série em branco = nota sem série).')
      return
    }
    setBuscando(true)
    try {
      const params: Record<string, string | number> = {}
      if (chaveDigitos) {
        params.chave = chaveDigitos
      } else {
        params.empcod = empresaCod ?? 1
        params.serie = serie.trim()
        params.numero = Number(numero)
        if (fornecedorCod) params.cliCod = fornecedorCod
      }
      const r = await api.get('/erp/estorno-nota', { params })
      setNota(r.data)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Nota não encontrada.')
    } finally {
      setBuscando(false)
    }
  }

  async function confirmarEstorno() {
    if (!nota) return
    setEstornando(true); setErro('')
    try {
      await api.post('/erp/estorno-nota', {
        empcod: nota.empcod, serie: nota.serie, numero: nota.numero, cliCod: nota.cliCod,
      })
      setSucesso(`Nota ${nota.serie}/${nota.numero} estornada com sucesso.`)
      setNota(null); setConfirmando(false)
      setChave(''); setSerie(''); setNumero(''); setFornecedorCod(''); setFornecedorDes('')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao estornar a nota.')
    } finally {
      setEstornando(false)
    }
  }

  return (
    <div>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.75rem' }}>Estorno de Nota Fiscal</h2>

      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.9rem' }}>
          <Campo rotulo="Chave NF-e/CT-e/NFS-e" width={470}>
            <input style={{ ...inp, fontFamily: 'monospace' }} value={chave} maxLength={50}
              onChange={e => setChave(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && buscar()}
              placeholder="44 dígitos (NF-e/CT-e) ou 50 (NFS-e)" />
          </Campo>
        </div>

        <div style={{ fontSize: '0.72rem', color: '#9ca3af', margin: '-0.5rem 0 0.9rem' }}>— ou —</div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Campo rotulo="Série" width={90}>
            <input style={inp} value={serie} onChange={e => setSerie(e.target.value)} />
          </Campo>
          <Campo rotulo="Número" width={130}>
            <input style={inp} inputMode="numeric" value={numero} onChange={e => setNumero(e.target.value.replace(/\D/g, ''))}
              onKeyDown={e => e.key === 'Enter' && buscar()} />
          </Campo>
          <Campo rotulo="Fornecedor (opcional)" width={340}>
            <LookupSelect
              endpoint="/erp/clientes/lista" codKey="cli_cod" descKey="cli_fan"
              codValue={fornecedorCod} descValue={fornecedorDes}
              onSelect={(cod, des) => { setFornecedorCod(cod); setFornecedorDes(des) }}
              placeholder="Buscar fornecedor..." listWidth={320} somenteComTexto
            />
          </Campo>
          <button className="btn btn-primary btn-sm" disabled={buscando} onClick={buscar} style={{ height: 32 }}>
            {buscando ? 'Buscando...' : 'Buscar'}
          </button>
        </div>
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}
      {sucesso && <div className="alert alert-success" style={{ marginBottom: '0.75rem' }}>✓ {sucesso}</div>}

      {nota && (
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem',
        }}>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1rem', fontSize: '0.85rem' }}>
            <div><strong>Estab.:</strong> {nota.empcod} &nbsp; <strong>Série:</strong> {nota.serie} &nbsp; <strong>Número:</strong> {nota.numero}</div>
            <div><strong>Fornecedor:</strong> {nota.cliNom} ({nota.cliCod})</div>
            <div><strong>Modelo:</strong> {nota.modelo}</div>
            <div><strong>Emissão:</strong> {fmtDataBr(nota.dataEmissao)} &nbsp; <strong>Entrada:</strong> {fmtDataBr(nota.dataEntrada)}</div>
            <div><strong>Valor:</strong> R$ {fmtMoeda(nota.valorTotal)}</div>
          </div>

          <table className="table" style={{ width: '100%', fontSize: '0.82rem', marginBottom: '1rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'center', width: 50 }}>Lin</th>
                <th style={{ textAlign: 'center', width: 90 }}>Produto</th>
                <th style={{ textAlign: 'left' }}>Descrição</th>
                <th style={{ textAlign: 'right', width: 100 }}>Qtde</th>
                <th style={{ textAlign: 'right', width: 120 }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {nota.itens.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1rem', color: '#9ca3af' }}>Sem itens lançados</td></tr>
              )}
              {nota.itens.map((it, idx) => (
                <tr key={it.linha} style={{ background: idx % 2 === 1 ? '#f8fafc' : '#fff' }}>
                  <td style={{ textAlign: 'center', color: '#6b7280' }}>{it.linha}</td>
                  <td style={{ textAlign: 'center', color: '#6b7280' }}>{it.itemCod}</td>
                  <td>{it.itemDes}</td>
                  <td style={{ textAlign: 'right' }}>{fmtQtd(it.quantidade)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#1a6ea8' }}>{fmtMoeda(it.valorTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {nota.ordensServico.length > 0 && (
            <div className="alert alert-warning" style={{ marginBottom: '0.75rem' }}>
              ⚠ Essa nota também vai mexer em Ordem(ns) de Serviço:
              <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.2rem' }}>
                {nota.ordensServico.map((os, idx) => (
                  <li key={idx}>
                    {os.acao === 'apaga_os'
                      ? <>OS <strong>{os.ordnum}</strong> foi gerada por essa nota — vai ser <strong>apagada por inteiro</strong>.</>
                      : <>OS <strong>{os.ordnum}</strong> já existia antes — só a linha <strong>{os.ordlin}</strong> (incluída por essa nota) vai ser removida.</>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {nota.bloqueada ? (
            <>
              {nota.avisoPeriodo && <div className="alert alert-warning">🔒 {nota.avisoPeriodo}</div>}
              {nota.motivoBloqueio && (
                <div className="alert alert-danger">
                  {nota.motivoBloqueio.split('\n').map((m, i) => <div key={i}>⚠ {m}</div>)}
                </div>
              )}
            </>
          ) : !confirmando ? (
            <button className="btn btn-danger btn-sm" onClick={() => setConfirmando(true)}>
              Estornar Nota
            </button>
          ) : (
            <div style={{
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6,
              padding: '0.85rem 1rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>⚠️</span>
                <span style={{ color: '#78350f', fontSize: '0.85rem', lineHeight: 1.5 }}>
                  Confirma o estorno da nota {nota.serie}/{nota.numero}? Essa ação apaga a nota, <strong>os itens e o financeiro dela</strong>
                  {nota.ordensServico.length > 0 && <> — e também <strong>a(s) Ordem(ns) de Serviço listada(s) acima</strong></>} — não tem como desfazer pela tela.
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.75rem', paddingLeft: '1.7rem' }}>
                <button className="btn btn-danger-solid btn-sm" disabled={estornando} onClick={confirmarEstorno}>
                  {estornando ? 'Estornando...' : 'Sim, confirmar estorno'}
                </button>
                <button className="btn btn-secondary btn-sm" disabled={estornando} onClick={() => setConfirmando(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
